import { InstrumentRow } from '@/helpers/portalInstrumentsHelper';
import useContextMenu from '@/utils/hooks/useContextMenu';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalInstrumentsTable from '@/utils/hooks/usePortalInstrumentsTable';
import { InstrumentFilters } from '@/XplorPortal/PortalInstruments';
import { useDisclosure } from '@mantine/hooks';
import { Dispatch, useCallback, useMemo, useState } from 'react';
import CurrentTradeDate from '../common/CurrentTradeDate';
import { InstrumentsTableFilters } from '../table/filters/InstrumentsTableFilters';
import PortalVirtualizedTable, { PortalTableGeneralProps } from '../table/PortalVirtualizedTable';
import TableControls from '../table/TableControls';
import TablePagination from '../table/TablePagination';

export interface PortalDepositRow {}

export interface PortalInstrumentsTableProps extends PortalTableGeneralProps {
    data: InstrumentRow[];
    filters: InstrumentFilters;
    pagination: { pageSize: number };
    setPagination: Dispatch<React.SetStateAction<{ pageSize: number }>>;
    setFilters: (filters: InstrumentFilters) => void;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const PortalInstrumentsTable = (props: PortalInstrumentsTableProps) => {
    const { data, pagination, setPagination, isLoading } = props;

    const lang = useLanguage();
    const [selectedRow, setSelectedRow] = useState<PortalDepositRow>();

    const contextMenu = useContextMenu();
    const filterDisclosure = useDisclosure(true);

    // const { filterDisclosure, viewDisclosure } = useTableDisclosure({ setSelectedRow });

    const onContextMenu = useCallback(
        (event, request) => {
            setSelectedRow(request);
            contextMenu.handleOpenContextMenu(event);
        },
        [contextMenu]
    );

    const { debouncedTableInstance, columns, globalFilter } = usePortalInstrumentsTable(props);

    const tableHeaders = useMemo(() => {
        return debouncedTableInstance.getLeafHeaders().map((header) => ({
            id: header.id,
            label: header.column.columnDef.header
        }));
    }, [debouncedTableInstance, isLoading, columns]);

    const excelInstrumentData = useMemo(() => {
        if (data && tableHeaders.length > 0) {
            return data.map((instrument) => {
                return tableHeaders.map((header) => instrument[header.id]);
            });
        } else return [];
    }, [data, tableHeaders]);

    const excelData = useMemo(() => {
        return {
            data: excelInstrumentData,
            fileName: 'Instruments',
            headerData: tableHeaders.map((header) => header.label)
        };
    }, [tableHeaders, excelInstrumentData]);

    return (
        <div className="flex flex-col gap-1 bg-brand-background-dark p-6 py-4 pb-6 border border-brand-neutral-700 text-brand-neutral-200 rounded-md overflow-hidden">
            <div className="flex flex-col h-full gap-4">
                <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center flex-wrap gap-2 sm:gap-5">
                        <div className="">{lang.portal.accountInstuments}</div>
                        {/* <div className="text-lg font-semibold text-neutral-200">{props.title}</div> */}
                        <TableControls
                            excelData={excelData as any}
                            filterDisclosure={filterDisclosure}
                            mutate={props.mutate}
                            isLoading={props.isLoading}
                            searchTitle={lang.blotters.refreshResults}
                        />
                    </div>
                    <InstrumentsTableFilters
                        {...props}
                        opened={filterDisclosure[0]}
                        globalFilter={globalFilter}
                        setGlobalFilter={debouncedTableInstance.setGlobalFilter}
                    />
                </div>
                <PortalVirtualizedTable tableInstance={debouncedTableInstance} isLoadingTableContent={isLoading} />
                <div className="flex flex-wrap-reverse gap-2 items-center justify-between">
                    <CurrentTradeDate />
                    <TablePagination
                        tableInstance={debouncedTableInstance}
                        manualPagination={pagination}
                        setManualPagination={setPagination}
                    />
                </div>
            </div>
        </div>
    );
};

export default PortalInstrumentsTable;
